import { useCallback } from 'react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';

import { createUser, getUsersList } from '@/features/users/users.service';
import {
  CurrentUser,
  UseUsersReturn,
  User,
} from '@/features/users/users.types';

import { useAuthUser } from '@/features/auth/auth.hook';
import { useOrgLocation } from '@/context/organization-location-context';

export function useUsers(): UseUsersReturn {
  const { selectedOrganisation, selectedLocation } = useOrgLocation();
  const queryClient = useQueryClient();

  // Fetch all users with React Query
  const {
    data: users = [],
    isLoading: loadingUsers,
    error: errorUsers,
    refetch: refreshUsersQuery,
  } = useQuery({
    queryKey: ['users'],
    queryFn: async () => {
      const { data } = await getUsersList({
        page: 1,
        perPage: 100,
        sort: 'first_name',
        order: 'ASC',
      });

      return data.map((user) => ({
        ...user,
        profile_image: user.profile_image || undefined,
        avatar: user.avatar || user.profile_image || undefined,
      }));
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
  });

  // Create usersById map for quick lookups
  const usersById = users.reduce(
    (acc, user) => {
      acc[user.id] = user;
      return acc;
    },
    {} as Record<string, User>,
  );

  const {
    data: currentUser,
    isLoading: loadingCurrentUser,
    error: errorCurrentUser,
    refetch: refreshCurrentUserQuery,
  } = useAuthUser();

  // Get user by ID (from local cache)
  const getUser = useCallback(
    (id: string): User | null => {
      return usersById[id] || null;
    },
    [usersById],
  );

  // Manual refresh function for users
  const refreshUsers = useCallback(async () => {
    await queryClient.invalidateQueries({
      queryKey: ['users'],
    });
    await refreshUsersQuery();
  }, [queryClient, refreshUsersQuery]);

  const fetchCurrentUser =
    useCallback(async (): Promise<CurrentUser | null> => {
      const { data } = await refreshCurrentUserQuery();

      return data?.user || null;
    }, [refreshCurrentUserQuery]);

  // Check if user exists by phone number
  const getUserByPhone = useCallback(
    async (phoneNumber: string): Promise<User | null> => {
      try {
        const { data } = await getUsersList({
          filter: {
            phone_number: phoneNumber,
            organisation_id: selectedOrganisation?.id || '',
            location_id: selectedLocation?.id || '',
          },
          page: 1,
          perPage: 1,
          sort: 'id',
          order: 'DESC',
        });

        return data.length > 0 ? data[0] : null;
      } catch (error) {
        console.error('Error checking user by phone:', error);
        return null;
      }
    },
    [],
  );

  // Create a new user
  const createUserMutation = useMutation({
    mutationFn: async (userData: Partial<User>): Promise<User> => {
      const { data } = await createUser(userData);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });

  const createUserFn = useCallback(
    async (userData: Partial<User>): Promise<User> => {
      return createUserMutation.mutateAsync(userData);
    },
    [createUserMutation],
  );

  return {
    users,
    usersById,
    loadingUsers,
    errorUsers: errorUsers as Error,
    currentUser: currentUser?.user || null,
    loadingCurrentUser,
    errorCurrentUser: errorCurrentUser as Error,
    getUser,
    refreshUsers,
    fetchCurrentUser,
    getUserByPhone,
    createUser: createUserFn,
  };
}
